import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import PublicLayout from "@/components/PublicLayout";
import { ArrowRight, Quotes, Camera } from "@phosphor-icons/react";

const ILLUSTRATION = "https://customer-assets-39nsmqrw.emergentagent.net/job_geo-photo-engine/artifacts/8w5x19na_photopulse.pro-illustration.png";

const H2 = ({ children }) => (
  <h2 className="mt-12 font-display text-2xl font-black tracking-tight text-zinc-900 sm:text-3xl">{children}</h2>
);
const P = ({ children }) => <p className="mt-4 text-[15px] leading-relaxed text-zinc-700">{children}</p>;
const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-zinc-700">
        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F27A54]" />{t}
      </li>
    ))}
  </ul>
);

export default function Article() {
  useSeo({
    title: "What & Why PhotoPulse? — PhotoPulse.pro",
    description: "Don't let your best photos die on your phone. Give your photographs a life, share your view of the world, and leave a visual legacy with PhotoPulse.",
    image: ILLUSTRATION,
  });

  return (
    <PublicLayout>
      {/* Header */}
      <section className="mx-auto max-w-3xl px-5 pt-14 text-center">
        <p className="overline text-[#F27A54]" data-testid="article-caption">What &amp; Why PhotoPulse?</p>
        <h1 className="mt-3 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl">
          Don't Let Your Best Photos Die on Your Phone
        </h1>
        <p className="mt-4 text-base text-muted-foreground sm:text-lg">
          Give Your Photographs a Life — and Leave a Visual Legacy with PhotoPulse™
        </p>
      </section>

      {/* Illustration */}
      <figure className="mx-auto mt-10 max-w-5xl px-5" data-testid="article-illustration">
        <img src={ILLUSTRATION} alt="How PhotoPulse gives your photos a second life — from your phone to the world"
          className="w-full rounded-xl border border-border shadow-sm" />
      </figure>

      {/* Body */}
      <article className="mx-auto max-w-3xl px-5 pb-8">
        <P>Every day, millions of us capture something beautiful.</P>
        <P>A spectacular sunset. A flower blooming in the garden. A waterfall discovered on vacation. A mountain covered in morning mist. An old village street. A beautiful beach. A bird outside the window. A memorable city skyline.</P>
        <P>We see something that moves us, reach for our phones, and take a photograph.</P>
        <P>And then what happens? Too often, nothing.</P>
        <P>The photograph joins hundreds—or thousands—of others stored on our phones and in cloud accounts. We may look at it once or twice. We may never even show it to our spouse, children, family or friends.</P>
        <P>Eventually, a remarkable collection of places, landscapes, flowers, wildlife, architecture and moments becomes almost invisible.</P>

        <blockquote className="mt-6 border-l-4 border-[#F27A54] bg-blue-50/50 py-4 pl-5 pr-4">
          <Quotes size={22} weight="fill" className="text-[#F27A54]" />
          <p className="mt-1 font-display text-lg font-bold text-zinc-900">Our phones have become, in a sense, cemeteries for beautiful photographs.</p>
          <p className="mt-1 text-sm text-zinc-600">PhotoPulse™ wants to change that.</p>
        </blockquote>

        <H2>Give Your Photos a Second Life</H2>
        <P>PhotoPulse.pro is being built around a simple idea: <strong>Don't let your best photos die on your phone.</strong></P>
        <P>Your photographs can have value far beyond the few seconds when you take them.</P>
        <Bullets items={[
          "A beautiful photograph can help another person discover a destination.",
          "A photograph of a flower can help someone appreciate a species they have never seen.",
          "A street scene can preserve what a neighborhood looked like before it changed.",
          "A photograph of a lake, mountain, beach or waterfall can inspire someone's next journey.",
          "And a photograph taken today could become part of the visual history future generations use to understand our world.",
        ]} />
        <P>PhotoPulse™ gives those photographs a place to live, be discovered, explored and enjoyed.</P>

        <H2>Your Photograph. Your Credit.</H2>
        <P>PhotoPulse is not simply about collecting photographs. It is about building a community of photographers and contributors.</P>
        <P>When your public photographs are used within the PhotoPulse and participating SocialPulse ecosystem, they can carry attribution linking back to you and your photographer profile. Your photograph becomes part of a larger knowledge network while preserving the connection to the person who captured it.</P>
        <P>You don't have to be a professional photographer. You simply need to have seen something worth sharing.</P>

        <H2>Turn an Ordinary Photo Into Useful Knowledge</H2>
        <P>One of the most exciting parts of PhotoPulse is the intelligence we can build around a photograph. Upload a photo, and PhotoPulse can use available metadata and AI-assisted analysis to help identify and organize information such as:</P>
        <Bullets items={[
          "Where was this taken?",
          "What is in the photograph?",
          "Is it a mountain, flower, lake, waterfall, beach, building or cityscape?",
          "What should the photograph be called?",
          "What tags will help people discover it?",
        ]} />
        <P>Instead of a file called <code className="rounded bg-zinc-100 px-1.5 py-0.5 text-sm font-semibold text-zinc-700">IMG_4837.JPG</code>, your photograph could become:</P>
        <p className="mt-3 rounded-lg bg-[#F27A54]/5 px-4 py-3 font-display text-lg font-bold text-[#F27A54]">Sunset Over the Arabian Sea — Kerala, India</p>
        <P>…with a useful description, geographic information, categories and searchable tags. Suddenly, a forgotten photograph becomes discoverable information.</P>

        <H2>Explore the World Through Other People's Eyes</H2>
        <P>PhotoPulse is also about discovery. The public PhotoPulse gallery allows visitors to explore photographs by keyword, place, category and tag. Featured collections can highlight themes such as:</P>
        <p className="mt-3 font-display font-bold text-zinc-800">Sunsets • Mountains • Cityscapes • Beaches • Forests • Lakes • Waterfalls</p>
        <P>A world photo map provides another way to explore. Imagine opening a map, selecting Kerala, Oregon, Switzerland, Japan or Australia and discovering photographs contributed by people who actually experienced those places.</P>
        <P>Over time, PhotoPulse can become a crowdsourced visual window into the world.</P>

        <H2>From One Photograph to an Entire Network</H2>
        <P>A photograph can become even more useful when connected to the right subject.</P>
        <Bullets items={[
          "A beautiful airport photograph might enrich travel content.",
          "A historic school photograph could support an alumni community.",
          "A photograph of a village could help document a geographic location.",
          "A food photograph could complement culinary information.",
          "A landscape could help travelers discover a destination.",
        ]} />
        <P>The broader vision is for approved public PhotoPulse photographs to become available across appropriate participating SocialPulse sites, while maintaining attribution and a link back to the photographer. This means one photograph doesn't have to disappear after a single social-media post — it can continue contributing to relevant places, topics and communities.</P>

        <H2>Powerful AI Helps Do the Work</H2>
        <P>Uploading photographs should not become another chore. PhotoPulse has been designed to automate much of the tedious organization. AI-assisted tools can help with:</P>
        <Bullets items={[
          "Subject identification", "Suggested titles", "Photo descriptions", "Keywords",
          "Smart tags", "Categories", "Text recognition through OCR",
          "Species-related identification and guidance",
          "Geographic organization when location information is available",
        ]} />
        <P>Importantly, AI processing can happen <strong>after</strong> the upload, so users don't have to sit and wait for every photograph to be analyzed before continuing.</P>

        <H2>Upload One Photo — or Hundreds</H2>
        <P>PhotoPulse is being designed for both the occasional photographer and people with enormous photo collections. Users can upload multiple photographs from their phones, use guided uploads, import from supported links and Google Photos, or use bulk-ingestion tools for larger collections.</P>
        <P>Large uploads can run as background jobs, with progress information and controls available to the user. The goal is simple: make bringing old photographs back to life as easy as possible.</P>

        <H2>Every Photograph Gets Its Own Home</H2>
        <P>Public photographs can have individual PhotoPulse pages with their title, description, location, tags and photographer attribution. PhotoPulse also automatically creates branded sharing cards designed for platforms such as Facebook, X and WhatsApp.</P>
        <P>So instead of sending someone an anonymous image file, you can share the photograph's permanent PhotoPulse page. Visitors can then discover the photographer and explore related photographs.</P>

        <H2>Built Around Places, Nature and the World Around Us</H2>
        <P>PhotoPulse's public collection focuses primarily on the world around us rather than becoming another people-centered social network. The public gallery is intended for subjects such as:</P>
        <p className="mt-3 text-[15px] leading-relaxed text-zinc-700">
          Nature • Landscapes • Sunsets &amp; Sunrises • Mountains • Beaches • Lakes &amp; Rivers • Waterfalls • Forests • Flowers &amp; Plants • Wildlife • Architecture • Villages • Cities &amp; Cityscapes • Historic Places • Travel Destinations
        </p>
        <P>People-focused photographs are excluded from the public PhotoPulse experience, helping maintain its focus on places, nature, discovery and visual knowledge.</P>

        <H2>Photographs Can Document Change</H2>
        <P>There is another important reason to preserve these images. The world changes.</P>
        <P>Buildings disappear. Neighborhoods are rebuilt. Forests change. Coastlines evolve. Businesses close. Roads are rerouted. Villages grow into towns. Gardens disappear. Landmarks are renovated.</P>
        <P>A photograph that seems ordinary today can become historically significant decades from now. When photographs are associated with place and time, they can collectively create an extraordinary visual record. Imagine looking at one location and seeing:</P>
        <p className="mt-3 rounded-lg bg-zinc-900 px-4 py-3 text-center font-display font-bold tracking-wide text-white">
          1985 → 1995 → 2005 → 2015 → 2025 → 2035
        </p>
        <P>PhotoPulse has the potential to help preserve that visual history.</P>

        <H2>Leave a Photographic Legacy</H2>
        <P>There is something even more personal behind PhotoPulse. Every photograph represents a decision: you noticed something. Among thousands of things around you, something caught your eye strongly enough that you stopped and captured it.</P>
        <P>That photograph therefore contains a little of your photographic mind — how you saw the world. Your collection of photographs can become a visual legacy.</P>
        <P>Years from now, your children, grandchildren and future generations may be able to see the landscapes, flowers, cities, villages and extraordinary moments that caught your attention. Instead of leaving thousands of forgotten files buried inside an old phone or cloud account, you can leave behind something organized, searchable and meaningful.</P>

        <H2>Help Us Photograph the World</H2>
        <P>Imagine millions of people contributing their best photographs.</P>
        <Bullets items={[
          "One person photographs a sunset in Kerala.",
          "Another captures Mount Fuji in Japan.",
          "Someone photographs a tiny village in Italy.",
          "Another documents wildflowers in Oregon.",
          "A traveler photographs a waterfall in Iceland.",
          "Someone else captures an old building before it disappears.",
        ]} />
        <P>Individually, they are photographs. Together, they can become something much larger: a crowdsourced photographic record of our world. A visual resource that helps us discover places, appreciate nature, document change and preserve what the world looked like for generations to come.</P>
        <P>Your photographs deserve more than being forgotten on your phone. Find them. Upload them. Describe them. Share them. Preserve them.</P>

        {/* Closing CTA */}
        <div className="mt-12 rounded-2xl bg-[#F27A54] p-8 text-center text-white">
          <p className="font-display text-2xl font-black tracking-tight">PhotoPulse™</p>
          <p className="mt-2 text-lg font-bold">Don't let your best photos die on your phone.</p>
          <p className="mt-1 text-sm text-white/80">Give your photos a life. Share your view of the world. Leave your photographic legacy.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/login" data-testid="article-contribute" className="flex items-center gap-1.5 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#F27A54] hover:bg-white/90">
              <Camera size={16} weight="fill" /> Start contributing <ArrowRight size={15} weight="bold" />
            </Link>
            <Link to="/explore" className="rounded-full bg-white/15 px-6 py-3 text-sm font-bold backdrop-blur hover:bg-white/25">Explore the gallery</Link>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
